import { type ChangeEvent, type FC, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { LinkButton } from '../../../components/Button/LinkButton'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { EventTypeAccess, MeetingMode } from '../../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useAddSchedulingEventTypesToUserPageMutation, useGetSchedulingEventTypesQuery } from '../../../store/scheduling/scheduling'
import { addToast } from '../../../store/toast/toastSlice'
import { OnboardingStepHeader } from '../../Growth/Onboarding/OnboardingStepHeader/OnboardingStepHeader'
import { OnboardingStepContent } from './OnboardingStepFrame'

const PAGE_SIZE = 5
const SCHEDULING_SETUP_PATH = '/workspace/setup/scheduling'

const MeetingModeChip: FC<{ meetingMode: MeetingMode }> = ({ meetingMode }) =>
  meetingMode === MeetingMode.IN_PERSON ? (
    <span className='paragraph-small zocks-bg-color-warning zocks-text-gray-900 rounded-3 px-2 py-1'>In person</span>
  ) : (
    <span className='paragraph-small zocks-bg-color-cyan zocks-text-dark-green rounded-3 px-2 py-1'>Virtual</span>
  )

export const EventTypesStep: FC = () => {
  const dispatch = useAppDispatch()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { hasScope } = useZocksAuth()
  const isAdmin = hasScope('**', 'account_config:update', ['account_config:admin'])

  const { data: eventTypes, isLoading } = useGetSchedulingEventTypesQuery({ accountId: authenticatedUser.accountId })
  const [addEventTypes, { isLoading: isAdding }] = useAddSchedulingEventTypesToUserPageMutation()

  const [query, setQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [page, setPage] = useState(0)

  const relevant = useMemo(() => (eventTypes ?? []).filter((eventType) => eventType.active && eventType.access === EventTypeAccess.ACCOUNT), [eventTypes])
  const filtered = useMemo(() => relevant.filter((eventType) => eventType.name.toLowerCase().includes(query.trim().toLowerCase())), [relevant, query])
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, pageCount - 1)
  const visible = filtered.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE)
  const allFilteredSelected = filtered.length > 0 && filtered.every((eventType) => selectedIds.includes(eventType.id))

  if (isLoading) {
    return null
  }

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.currentTarget.value)
    setPage(0)
  }

  const toggle = (id: string) => setSelectedIds((ids) => (ids.includes(id) ? ids.filter((current) => current !== id) : [...ids, id]))
  const toggleAll = () => setSelectedIds(allFilteredSelected ? [] : filtered.map((eventType) => eventType.id))

  const handleAdd = async () => {
    const result = await addEventTypes({ accountId: authenticatedUser.accountId, userId: authenticatedUser.id, eventTypeIds: selectedIds })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not add event types', messages: ['Please try again.'] }))
    }
  }

  if (relevant.length === 0) {
    return (
      <OnboardingStepHeader
        icon={<i className='bi bi-calendar2-check' />}
        title='No active event types yet'
        subtitle={
          isAdmin
            ? 'Event types are the meetings clients book with you. Scheduling needs at least one before your team can take bookings. Set up new ones or activate what you have.'
            : 'Event types are the meetings clients book with you. Scheduling needs at least one before you can take bookings. Ask your account owner to make some available.'
        }
      >
        {isAdmin ? (
          <LinkButton to={SCHEDULING_SETUP_PATH} variant='primaryLinkButton' className='text-decoration-none'>
            Set up shared event types
          </LinkButton>
        ) : null}
      </OnboardingStepHeader>
    )
  }

  return (
    <>
      <OnboardingStepHeader
        icon={<i className='bi bi-calendar2-check' />}
        title='Add event types to your booking page'
        subtitle="Choose which of your account's shared event types clients can book with you."
      />
      <OnboardingStepContent>
        <div className='w-100 border zocks-border-color-gray-300 rounded-3 d-flex flex-column overflow-hidden'>
          <div className='zocks-p-1.25 border-bottom zocks-border-color-gray-200'>
            <div className='d-flex align-items-center zocks-gap-0.5 border rounded-1 zocks-border-color-gray-300 zocks-px-0.75'>
              <i className='bi bi-search zocks-text-gray-500' />
              <Form.Control
                type='search'
                placeholder='Search'
                aria-label='Search event types'
                value={query}
                onChange={onSearchChange}
                className='border-0 px-0'
              />
            </div>
          </div>
          <div className='d-flex align-items-center zocks-gap-0.5 zocks-px-1.25 zocks-py-0.75 border-bottom zocks-border-color-gray-200'>
            <Button variant='primaryLinkButton' className='p-0' disabled={filtered.length === 0} onClick={toggleAll}>
              Select all
            </Button>
            <span className='zocks-text-gray-400'>&middot;</span>
            <span className='paragraph-small zocks-text-gray-600'>{selectedIds.length} selected</span>
          </div>
          <div className='d-flex flex-column'>
            {visible.map((eventType) => (
              <label
                key={eventType.id}
                htmlFor={`event-type-${eventType.id}`}
                className='d-flex align-items-start zocks-gap-0.75 zocks-px-1.25 py-3 border-bottom zocks-border-color-gray-200 mb-0'
              >
                <Form.Check
                  id={`event-type-${eventType.id}`}
                  type='checkbox'
                  className='mt-1'
                  checked={selectedIds.includes(eventType.id)}
                  onChange={() => toggle(eventType.id)}
                />
                <div className='d-flex flex-column flex-grow-1 zocks-gap-0.25'>
                  <span className='d-flex align-items-center flex-wrap zocks-gap-0.5'>
                    <span className='bold'>{eventType.name}</span>
                    <span className='zocks-text-gray-600'>&middot; {eventType.durationMinutes} min &middot;</span>
                    <MeetingModeChip meetingMode={eventType.meetingMode} />
                  </span>
                  {eventType.description ? <span className='paragraph-small zocks-text-gray-600'>{eventType.description}</span> : null}
                </div>
              </label>
            ))}
          </div>
          <div className='d-flex align-items-center justify-content-between zocks-px-1.25 zocks-py-0.75'>
            <span className='paragraph-small zocks-text-gray-600'>
              Showing {visible.length} of {filtered.length}
            </span>
            <div className='d-flex zocks-gap-0.25'>
              <Button variant='grayGhost' className='p-1' aria-label='Previous page' disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>
                <i className='bi bi-chevron-left' />
              </Button>
              <Button
                variant='grayGhost'
                className='p-1'
                aria-label='Next page'
                disabled={currentPage >= pageCount - 1}
                onClick={() => setPage(currentPage + 1)}
              >
                <i className='bi bi-chevron-right' />
              </Button>
            </div>
          </div>
        </div>
        <Button
          variant='primary'
          className='align-self-stretch'
          disabled={selectedIds.length === 0 || isAdding}
          loading={isAdding}
          onClick={() => void handleAdd()}
        >
          Add event types
        </Button>
        {isAdmin ? (
          <LinkButton to={SCHEDULING_SETUP_PATH} variant='primaryLinkButton' className='text-decoration-none'>
            Manage your account's event types
          </LinkButton>
        ) : null}
      </OnboardingStepContent>
    </>
  )
}
